import { AxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Col, Form, Input, Layout, Row, Steps } from 'antd';
import { FilterOperatorsEnum, IInventoryEntity, IItemsWithCount, InventoryStatusEnum } from '@rasayi-workspace/shared';
import { BASE_QUERY_OPTIONS } from '@constants';
import { GetItems } from '@services';
import { BaseLayoutComponent, SearchableDropdown } from '@components';
import { orderContext } from 'src/app/contexts/order';
import { OrderDetailPageComponent } from '../order-details';
import { DeliveryInformationPageComponent } from '../delivery-information';
import { isArray } from 'class-validator';

const { Content } = Layout;

export const ProductInformationPageComponent = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [form] = Form.useForm<Partial<any>>();
    const { inventories, setInventories, orderDetails } = useContext(orderContext);
    const [rawInventories, setRawInventories] = useState<IInventoryEntity[]>([]);
    const [selectedInventory, setSelectedInventory] = useState<IInventoryEntity[]>([]);
    const [nextPage, setNextPage] = useState(false);
    const [previousPage, setPreviousPage] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isValidForm, setIsValidForm] = useState(true);


    const { refetch: fetchInventory, isFetching: isInventoryFetching } = useQuery<IItemsWithCount<IInventoryEntity>, AxiosError>({
        ...BASE_QUERY_OPTIONS,
        queryKey: ['getInventory'],
        queryFn: () => GetItems<IInventoryEntity>(
            'inventory',
            ['mobile_info', 'procurement.brand', 'procurement.product_type', 'procurement.vendor'],
            ['id', 'internal_id', 'purchase_price', 'status'],
            [
                [
                    {
                        field: 'status',
                        operator: FilterOperatorsEnum.EQUAL,
                        value: InventoryStatusEnum.UNSOLD
                    }
                ]
            ]
        ),
        onSuccess: (returnedResult: IItemsWithCount<IInventoryEntity>): void => setRawInventories(returnedResult.items),
        onError: () => setRawInventories([])
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateFormFields = (property: string | number | any, value: string | number | any) => form.setFieldsValue({ [property]: value });

    const refetechHandler = () => {
        fetchInventory();
    };

    const previousPageHandler = () => {
        setPreviousPage(true);
    };

    const handleFormChangeMain = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let validValues = selectedInventory?.length ? !selectedInventory.some((value: any) => value.retail_price === undefined) : true;

        if (validValues && selectedInventory?.length) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const sumRetail = selectedInventory.reduce((sum, value: any) => {
                if (value.retail_price)
                    return sum + value.retail_price;
                return sum;
            }, 0);
            if (sumRetail !== parseInt(String(orderDetails.total_retail_price)))
                validValues = false;
        }

        setIsValidForm(validValues);
    };

    
    useEffect(() => {
        !isInventoryFetching && fetchInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (rawInventories?.length && inventories?.length) {
            setSelectedInventory(inventories);
        } else {
            setSelectedInventory([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inventories, rawInventories]);

    useEffect(() => {
        handleFormChangeMain();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInventory]);

    return (
        <>
            {
                nextPage &&
                <DeliveryInformationPageComponent />
            }
            {
                previousPage &&
                <OrderDetailPageComponent />
            }
            <BaseLayoutComponent children={
                <Content className='m-5 bg-white border rounded-lg'>
                    <Steps
                        style={{ marginTop: 20 }}
                        progressDot
                        current={2}
                        items={[
                            {
                                title: 'Customer Information',
                            },
                            {
                                title: 'Order Details',
                            },
                            {
                                title: 'Product Information',
                            },
                            {
                                title: 'Delivery Information',
                            },
                            {
                                title: 'Guarantor Information',
                            },
                            {
                                title: 'Complete',
                            },
                        ]}
                    />
                    <Card
                        title='Product Information'
                        extra={<>
                            <Button
                                key='previousPage'
                                type='primary'
                                onClick={previousPageHandler}
                                style={{ marginLeft: 4 }}
                            >
                                Previous Page
                            </Button>
                            <Button
                                key='nextPage'
                                type='primary'
                                onClick={() => setNextPage(true)}
                                style={{ marginLeft: 4 }}
                               // disabled={!isValidForm}
                            >
                                Next
                            </Button>
                        </>
                        }
                        style={{ height: 600, overflow: 'scroll' }}
                    >
                        <div className='flex justify-end'>
                            <Button
                                key='link'
                                type='primary'
                                onClick={refetechHandler}
                            >
                                Refetch Inventory
                            </Button>
                        </div>
                        <Form
                            form={form}
                        >
                            <Row justify={'center'} gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name='internal_id'
                                        label='Inventory Id'
                                        rules={[
                                            { required: false, message: 'Please enter the inventory_ids' }
                                        ]}
                                        // initialValue={inventories.map((inventory: IInventoryEntity) => inventory.internal_id)}
                                    >
                                        {
                                            SearchableDropdown<IInventoryEntity>(
                                                {
                                                    key: 'internal_id',
                                                    mode: 'multiple',
                                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                    defaultMultiValue: inventories.map((inventory: any) => inventory.internal_id),
                                                    sortBy: { 'internal_id': 'ASC' },
                                                    value: 'internal_id',
                                                    queryKey: ['getInventory'],
                                                    queryFnParams: {
                                                        apiRoute: 'inventory',
                                                        relations: ['mobile_info', 'procurement.brand', 'procurement.product_type', 'procurement.vendor'],
                                                        fields: ['id', 'internal_id', 'purchase_price', 'status'],
                                                        filter: {
                                                            field: 'internal_id',
                                                            operator: FilterOperatorsEnum.LIKE,
                                                            value: ''
                                                        },
                                                        predefinedFilters: [
                                                            {
                                                                field: 'status',
                                                                operator: FilterOperatorsEnum.EQUAL,
                                                                value: InventoryStatusEnum.UNSOLD
                                                            }
                                                        ]
                                                    },
                                                    onSelectionChange: (selectedItemIds: string | string[], _, items) => {
                                                        if (isArray(selectedItemIds)) {
                                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                            const selectedOption: IInventoryEntity[] = items.filter((inventory: any) => selectedItemIds.includes(inventory.internal_id));
                                                            setSelectedInventory(selectedOption);
                                                            setInventories(selectedOption);
                                                        }
                                                    }
                                                }
                                            )
                                        }
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify={'center'} gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name='total_retail_price'
                                        label="Order's Total Retail Price"
                                        initialValue={orderDetails.total_retail_price}
                                    >
                                        <Input disabled value={orderDetails.total_retail_price} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <p className='d-flex text-center'>
                                <b>Note: </b>
                                Retail prices should be Equal to Order's Total Retail Price
                            </p>
                            {selectedInventory.map((inventoryItem, index) => {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                const item = inventoryItem as any;
                                return (
                                <div key={index}>
                                    <Row justify={'center'} gutter={16}>
                                        <p style={{ fontSize: 20 }}> Product {index + 1}</p>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                key={`retail_price_${item.internal_id}`}
                                                label={`Enter Retail Price (${index + 1})`}
                                                id={item.internal_id}
                                                required={!!selectedInventory.length}
                                                rules={[
                                                    { required: true, message: 'Please enter the retail_price_' }
                                                ]}
                                                initialValue={item?.retail_price ? String(item?.retail_price) : undefined}
                                            >
                                                <Input
                                                    value={item?.retail_price ? String(item?.retail_price) : undefined}
                                                    onChange={(event) => {
                                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                        const tempInventory: IInventoryEntity[] = selectedInventory.reduce((finalInventory: IInventoryEntity[], currentInventory: any) => {
                                                            if (currentInventory.internal_id === item.internal_id) {
                                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                                finalInventory.push({ ...currentInventory, retail_price: parseInt(event?.target?.value) || undefined } as any);
                                                            } else
                                                                finalInventory.push(currentInventory);
                                                            return finalInventory;
                                                        }, []);
                                                        setSelectedInventory(tempInventory);
                                                        setInventories(tempInventory);
                                                    }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                key={`mobile_info.imei_1_${index}`}
                                                label={`imei_1 (${index + 1})`}
                                                initialValue={item?.mobile_info?.imei_1}
                                            >
                                                <Input disabled value={item?.mobile_info?.imei_1} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                key={`mobile_info.imei_2_${index}`}
                                                label={`imei_2 (${index + 1})`}
                                                initialValue={item?.mobile_info?.imei_2}
                                            >
                                                <Input disabled value={item?.mobile_info?.imei_2} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                key={`locking_identifier_${index}`}
                                                label={`Locking Identifier (${index + 1})`}
                                                rules={[
                                                    { required: true, message: 'Please enter the locking_identifier' },
                                                    { min: 2, message: 'At least 2 characters' }
                                                ]}
                                                initialValue={item?.mobile_info?.locking_identifier}
                                            >
                                                <Input disabled value={item?.mobile_info?.locking_identifier}
                                                    onChange={(event) => updateFormFields('locking_identifier', event?.target.value)} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                key={`total_purchase_price_${index}`}
                                                label={`Purchase Price (${index + 1})`}
                                                // initialValue={item?.procurement?.total_purchase_price}
                                            >
                                                <Input disabled 
                                                // value={item?.procurement?.total_purchase_price}
                                                 />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                key={`brand_name_${index}`}
                                                label={`Brand Name (${index + 1})`}
                                                // initialValue={item?.procurement?.brand?.name}
                                            >
                                                <Input disabled 
                                                // value={item?.procurement?.brand?.name}
                                                    onChange={(event) => updateFormFields('brand.name', event?.target.value)} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                key={`vendor_name_${index}`}
                                                label={`Vendor Name (${index + 1})`}
                                                initialValue={item?.procurement?.vendor?.vendor_name}
                                            >
                                                <Input disabled value={item?.procurement?.vendor?.vendor_name}
                                                    onChange={(event) => updateFormFields('vendor.vendor_name', event?.target.value)} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                key={`model_${index}`}
                                                label={`Model (${index + 1})`}
                                                // initialValue={item?.procurement?.model}
                                            >
                                                <Input disabled
                                                //  value={item?.procurement?.model} 
                                                 />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                key={`product_type_${index}`}
                                                label={`Product Type (${index + 1})`}
                                                // initialValue={item?.procurement?.product_type?.name}
                                            >
                                                <Input disabled 
                                                // value={item?.procurement?.product_type?.name}
                                                    onChange={(event) => updateFormFields('product_type.name', event?.target.value)} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </div>
                                );
                            })}
                        </Form>
                    </Card>
                </Content>
            }
            />
        </>
    );
};